import React, { useState, useEffect, useMemo } from 'react';
import { motion, Reorder } from 'framer-motion';
import {
  Calendar,
  Filter,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Clock,
  Layers,
  GripVertical,
  Plus
} from 'lucide-react';
import { syncManager } from '../services/sync';
import { config } from '../config';
import { COMMERCIALIZATION_AREAS } from '../data/dailyFocusItems'; // ADDITION (v2.0): 18-area commercialization structure

interface Task {
  id: string;
  title: string;
  department: 'Marketing & Sales' | 'Customer Fulfillment' | 'Finance Management' | 'Human Capital Management' | 'Operations Management' | 'Executive & Admin Management';
  area: string;
  horizon: 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Yearly' | 'Long-term';
  priority: 'High' | 'Medium' | 'Low';
  status: 'Pending' | 'In-Progress' | 'Completed';
  law: string; // Which of the 7 Laws this task aligns with
  assignedTo: string;
  sortOrder: number;
}

/** 6 LifeOS Keep departments */
const DEPARTMENTS = ['Marketing & Sales', 'Customer Fulfillment', 'Finance Management', 'Human Capital Management', 'Operations Management', 'Executive & Admin Management'] as const;

/** ADDITION (v2.0): New 6-department LifeOS Keep commercialization structure */
const COMMERCIALIZATION_DEPARTMENTS = [
  { id: 'SSM', label: 'Marketing & Sales' },
  { id: 'CF', label: 'Customer Fulfillment' },
  { id: 'FM', label: 'Finance Management' },
  { id: 'HCM', label: 'Human Capital Management' },
  { id: 'OM', label: 'Operations Management' },
  { id: 'EAM', label: 'Executive & Admin Management' },
] as const;
const HORIZONS = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly', 'Long-term'] as const;

const OPERATIONAL_AREAS = {
  'Marketing & Sales': ['Business Development', 'Marketing', 'Sales', 'Referral Partners'],
  'Customer Fulfillment': ['CJMS Delivery', 'Service Delivery Partners', 'Customer Success'],
  'Finance Management': ['Pricing & Billing', 'Partner Payments', 'Revenue & Expense Management', 'Corporate Governance'],
  'Human Capital Management': ['Talent Management', 'Partner Onboarding & Certification', 'Team & Culture', 'Compliance & Wellbeing'],
  'Operations Management': ['PWA Platform', 'AI Engine', 'Data & Sync', 'Process Scalability', 'Client Lifecycle'],
  'Executive & Admin Management': ['Strategic Planning', 'Legal & Compliance', 'Admin Operations'],
};

const FocusView: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [activeHorizon, setActiveHorizon] = useState<string>('Daily');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterDept, setFilterDept] = useState<string | 'All'>('All');

  useEffect(() => {
    const fetchTasks = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await syncManager.fetchTabData('Master_Tasks', config.ADMIN_EMAIL as any);

        if (data && Array.isArray(data)) {
          const formattedTasks: Task[] = data.map((row: any, index: number) => ({
            id: row.ID || Math.random().toString(36).substr(2, 9),
            title: row.TaskName || 'Untitled Task',
            department: row.Department,
            area: row.Area,
            horizon: row.Horizon,
            priority: row.Priority,
            status: row.Status,
            law: row.Law || 'Law 1: Goals & Plans',
            assignedTo: row.Owner || 'Sipho',
            sortOrder: index
          }));
          setTasks(formattedTasks);
        } else {
          // Fallback mock data so the view always renders something useful
          setTasks([
            {
              id: '1', title: 'Optimize Lead Capture Funnel',
              department: 'Marketing & Sales', area: 'Marketing',
              horizon: 'Daily', priority: 'High', status: 'In-Progress',
              law: 'Law 2: Education, Skills & Traits Development', assignedTo: 'Sipho', sortOrder: 0
            },
            {
              id: '2', title: 'Reconcile Q1 Finance Statements',
              department: 'Finance Management', area: 'Finance Management',
              horizon: 'Monthly', priority: 'Medium', status: 'Pending',
              law: 'Law 5: Obstacle Overcoming', assignedTo: 'Finance Agent', sortOrder: 1
            },
            {
              id: '3', title: 'Review Partner Network Onboarding',
              department: 'Marketing & Sales', area: 'Referral Partners',
              horizon: 'Weekly', priority: 'High', status: 'Completed',
              law: 'Law 1: Goals & Plans', assignedTo: 'Sipho', sortOrder: 2
            },
          ]);
        }
      } catch (err) {
        console.error('Failed to fetch tasks:', err);
        setError('Unable to load live tasks. Showing defaults.');
        // Ensure fallback data is shown on error
        setTasks([
            {
              id: '1', title: 'Optimize Lead Capture Funnel',
              department: 'Marketing & Sales', area: 'Marketing',
              horizon: 'Daily', priority: 'High', status: 'In-Progress',
              law: 'Law 2: Education, Skills & Traits Development', assignedTo: 'Sipho', sortOrder: 0
            },
            {
              id: '2', title: 'Reconcile Q1 Finance Statements',
              department: 'Finance Management', area: 'Finance Management',
              horizon: 'Monthly', priority: 'Medium', status: 'Pending',
              law: 'Law 5: Obstacle Overcoming', assignedTo: 'Finance Agent', sortOrder: 1
            },
            {
              id: '3', title: 'Review Partner Network Onboarding',
              department: 'Marketing & Sales', area: 'Referral Partners',
              horizon: 'Weekly', priority: 'High', status: 'Completed',
              law: 'Law 1: Goals & Plans', assignedTo: 'Sipho', sortOrder: 2
            },
          ]);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  const handleReorder = (newFilteredOrder: Task[]) => {
    setTasks(prevTasks => {
      const result = [...prevTasks];
      const filteredIndices = prevTasks
        .map((t, i) => (t.horizon === activeHorizon && (filterDept === 'All' || t.department === filterDept)) ? i : -1)
        .filter(i => i !== -1);

      newFilteredOrder.forEach((task, i) => {
        if (filteredIndices[i] !== undefined) {
          result[filteredIndices[i]] = { ...task, sortOrder: i };
        }
      });

      return result;
    });
  };

  const filteredTasks = useMemo(() => {
    return tasks
      .filter(t => t.horizon === activeHorizon && (filterDept === 'All' || t.department === filterDept))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [tasks, activeHorizon, filterDept]);

  return (
    <div className="space-y-8 p-4 max-w-7xl mx-auto">
      <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-black text-slate-800 uppercase tracking-tighter">
            Control Room
          </h1>
          <p className="text-slate-500 font-medium text-sm">
            Focus View: 7 Laws Framework & Operational Alignment
          </p>
        </div>

        <div className="flex items-center gap-3 bg-white p-1 rounded-2xl shadow-sm border border-slate-100">
          {HORIZONS.map(h => (
            <button
              key={h}
              onClick={() => setActiveHorizon(h)}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                activeHorizon === h
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {h}
            </button>
          ))}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Department Sidebar/Filter */}
        <aside className="lg:col-span-1 space-y-4">
          <div className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100 space-y-4">
            <div className="flex items-center gap-2 text-slate-800 font-bold text-sm uppercase tracking-wider">
              <Filter size={16} />
              <span>Departments</span>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => setFilterDept('All')}
                className={`w-full text-left px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                  filterDept === 'All' ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                All Departments
              </button>
              {DEPARTMENTS.map(dept => (
                <button
                  key={dept}
                  onClick={() => setFilterDept(dept)}
                  className={`w-full text-left px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    filterDept === dept ? 'bg-blue-50 text-blue-600' : 'text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {dept}
                </button>
              ))}
              {/* ADDITION (v2.0): New commercialization departments */}
              <div className="pt-2 mt-2 border-t border-slate-100">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 px-4">Commercialization</p>
                {COMMERCIALIZATION_DEPARTMENTS.map(d => (
                  <button
                    key={d.id}
                    onClick={() => setFilterDept(d.label)}
                    className={`w-full text-left px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      filterDept === d.label ? 'bg-emerald-50 text-emerald-600' : 'text-slate-500 hover:bg-slate-50'
                    }`}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-blue-600 p-6 rounded-[2rem] shadow-lg text-white space-y-4">
            <div className="flex items-center gap-2 font-bold text-xs uppercase tracking-widest opacity-80">
              <Layers size={16} />
              <span>7 Laws Engine</span>
            </div>
            <p className="text-sm font-medium leading-relaxed">
              Tasks are routed based on the 7 Laws of Business Success. Current alignment: <span className="font-bold text-blue-200">Optimal</span>
            </p>
            <div className="pt-2 flex items-center justify-between text-[10px] font-bold opacity-70">
              <span>Law 1: Goals & Plans</span>
              <ChevronRight size={12} />
            </div>
          </div>
        </aside>

        {/* Main Task Area */}
        <main className="lg:col-span-3 space-y-6">
          {loading && (
            <div className="h-64 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
            </div>
          )}

          {error && !loading && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <p className="text-amber-700 text-sm font-medium">{error}</p>
            </div>
          )}

          {!loading && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-800">
                  {filterDept === 'All' ? 'Global Focus' : filterDept} <span className="text-slate-400 font-medium text-sm">({activeHorizon})</span>
                </h2>
                <button className="p-2 bg-blue-600 text-white rounded-full shadow-md hover:bg-blue-700 transition-colors">
                  <Plus size={20} />
                </button>
              </div>

              <div className="grid grid-cols-1 gap-3">
                {filteredTasks.length === 0 ? (
                  <div className="text-center py-20 bg-slate-50 rounded-[2rem] border-2 border-dashed border-slate-200">
                    <Calendar className="mx-auto text-slate-300 mb-4" size={48} />
                    <p className="text-slate-500 font-medium">No tasks found for this horizon and department.</p>
                  </div>
                ) : (
                  <Reorder.Group axis="y" values={filteredTasks} onReorder={handleReorder}>
                    {filteredTasks.map((task) => (
                      <Reorder.Item
                        key={task.id}
                        value={task}
                        className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4 group hover:border-blue-200 transition-all cursor-grab active:cursor-grabbing"
                      >
                        <div className="text-slate-300 group-hover:text-slate-400 transition-colors">
                          <GripVertical size={18} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                              task.priority === 'High' ? 'bg-rose-50 text-rose-600' :
                              task.priority === 'Medium' ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'
                            }`}>
                              {task.priority}
                            </span>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tighter">
                              {task.area}
                            </span>
                          </div>
                          <h4 className="font-bold text-slate-800 text-sm truncate">
                            {task.title}
                          </h4>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="hidden md:flex flex-col items-end">
                            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-tight flex items-center gap-1">
                              <Layers size={10} />
                              {task.law}
                            </span>
                            <span className="text-[10px] text-slate-400 font-medium">
                              {task.assignedTo}
                            </span>
                          </div>

                          <div className="p-2 rounded-xl bg-slate-50 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600 transition-all cursor-pointer">
                            <CheckCircle2 size={20} />
                          </div>
                        </div>
                      </Reorder.Item>
                    ))}
                  </Reorder.Group>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default FocusView;
